import { Injectable } from '@angular/core';
import { Profile, SignoutResponse, User, UserManager } from 'oidc-client';
import { BehaviorSubject, concat, from, lastValueFrom, mergeMap, Observable, of } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';

import { ConfigService } from '../../tms-shared/services/config.service';
import { JwtHelperService } from './jwt-helper.service';
import { LogoutService } from './logout.service';
import { ApplicationPaths, Roles } from '../constants';

export type IAuthenticationResult =
  | SuccessAuthenticationResult
  | FailureAuthenticationResult
  | RedirectAuthenticationResult;

export interface SuccessAuthenticationResult {
  status: AuthenticationResultStatus.Success;
  state: any;
}

export interface FailureAuthenticationResult {
  status: AuthenticationResultStatus.Fail;
  message: string;
}

export interface RedirectAuthenticationResult {
  status: AuthenticationResultStatus.Redirect;
}

export enum AuthenticationResultStatus {
  Success,
  Redirect,
  Fail,
}

export interface IUser extends Profile {
  clientId: string;
  role: string[];
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  homeRoute = '';

  // By default, pop-ups are disabled because they don't work properly on Edge. If you want to enable pop up authentication simply set this flag to false.
  private popUpDisabled = true;
  private _userManager: UserManager;
  private userSubject: BehaviorSubject<IUser> = new BehaviorSubject(undefined);
  private _user: IUser;

  constructor(
    private configService: ConfigService,
    private loginService: LogoutService,
  ) {}

  isAuthenticated(): Observable<boolean> {
    return this.getUser().pipe(map((u: IUser) => !!u));
  }

  getUser(): Observable<IUser> {
    return concat(
      this.userSubject.pipe(
        take(1),
        filter((u) => !!u),
        tap((u) => (this._user = u)),
      ),
      this.getUserFromStorage().pipe(
        filter((u: IUser) => !!u),
        tap((u) => {
          this._user = u;
          this.userSubject.next(u);
        }),
      ),
      this.userSubject.asObservable(),
    );
  }

  setUser(user: IUser): Observable<IUser> {
    this.userSubject.next(user);
    return this.getUser();
  }

  get customerId(): string {
    return this._user.customer_id;
  }

  get customerName(): string {
    return this._user.customer_name;
  }

  get isCustomer(): boolean {
    return !!this._user?.customer_id;
  }

  getCanAmendComments(): boolean {
    return this._user?.amend_other_comments === 'true';
  }

  get showSettings(): boolean {
    return this._user?.show_settings === 'true';
  }

  get isLoggedIn(): boolean {
    return !!this._user;
  }

  get userId(): string {
    return this._user?.sub;
  }

  get user(): IUser {
    return this._user;
  }

  get tokenKey(): string {
    return `oidc.user:${this.configService.config.identityServerUrl}:${this.configService.config.applicationName}`;
  }

  get isAdmin(): boolean {
    return this.hasRole(Roles.Admin);
  }

  get tokenEndpoint(): string {
    return `${this.configService.config.identityServerUrl}/connect/token`;
  }

  hasRole(role: string): boolean {
    return (
      this._user?.role &&
      (Array.isArray(this._user.role) ? this._user.role.some((x) => x === role) : this._user.role === role)
    );
  }

  getAccessToken(): Observable<string> {
    return from(this.ensureUserManagerInitialized()).pipe(
      mergeMap(() => from(this._userManager.getUser())),
      map((user) => user && user.access_token),
    );
  }

  // We try to authenticate the user in three different ways:
  // 1) We try to see if we can authenticate the user silently. This happens when the user is already logged in on the IdP and is done using a hidden iframe on the client.
  // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a Pop-Up blocker or the user has disabled PopUps.
  // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional redirect flow.
  async signIn(state: any): Promise<IAuthenticationResult> {
    await this.ensureUserManagerInitialized();
    let user: User = undefined;
    try {
      user = await this._userManager.signinSilent(this.createArguments());
      this.userSubject.next(user.profile as IUser);
      return this.success(state);
    } catch (silentError) {
      // User might not be authenticated, fallback to popup authentication
      console.log('Silent authentication error: ', silentError);

      try {
        if (this.popUpDisabled) {
          throw new Error("Popup disabled. Change 'auth.service.ts:popupDisabled' to false to enable it.");
        }
        user = await this._userManager.signinPopup(this.createArguments());
        this.userSubject.next(user.profile as IUser);
        return this.success(state);
      } catch (popupError) {
        if (popupError.message === 'Popup window closed') {
          // The user explicitly cancelled the login action by closing an opened popup.
          return this.error('The user closed the window.');
        } else if (!this.popUpDisabled) {
          console.log('Popup authentication error: ', popupError);
        }

        // PopUps might be blocked by the user, fallback to redirect
        try {
          await this._userManager.signinRedirect(this.createArguments(state));
          return this.redirect();
        } catch (redirectError) {
          console.log('Redirect authentication error: ', redirectError);
          return this.error(redirectError);
        }
      }
    }
  }

  async completeSignIn(url: string): Promise<IAuthenticationResult> {
    try {
      await this.ensureUserManagerInitialized();
      const user = await this._userManager.signinCallback(url);
      this.userSubject.next(user && (user.profile as IUser));
      return this.success(user && user.state);
    } catch (error) {
      console.log('There was an error signing in: ', error);
      return this.error('There was an error signing in.');
    }
  }

  async signOut(state: any): Promise<IAuthenticationResult> {
    try {
      if (this.popUpDisabled) {
        throw new Error("Popup disabled. Change 'authorize.service.ts:popupDisabled' to false to enable it.");
      }
      await this.ensureUserManagerInitialized();
      await this._userManager.signoutPopup(this.createArguments());
      this.userSubject.next(undefined);
      return this.success(state);
    } catch (popupSignOutError) {
      console.log('Popup signout error: ', popupSignOutError);
      try {
        await lastValueFrom(this.loginService.logout());
        await this._userManager.signoutRedirect(this.createArguments(state));
        return this.redirect();
      } catch (redirectSignOutError) {
        console.log('Redirect signout error: ', popupSignOutError);
        return this.error(redirectSignOutError);
      }
    }
  }

  async completeSignOut(url: string): Promise<IAuthenticationResult> {
    await this.ensureUserManagerInitialized();
    try {
      const state: SignoutResponse | any = await this._userManager.signoutCallback(url);
      this.userSubject.next(undefined);
      sessionStorage.clear();
      return this.success(state && state.data);
    } catch (error) {
      console.log(`There was an error trying to log out '${error}'.`);
      return this.error(error);
    }
  }

  private createArguments(state?: any): any {
    return { useReplaceToNavigate: true, data: state };
  }

  private error(message: string): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Fail, message };
  }

  private success(state: any): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Success, state };
  }

  private redirect(): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Redirect };
  }

  private async ensureUserManagerInitialized(): Promise<void> {
    if (this._userManager !== undefined) {
      return;
    }

    if (!this.configService.config.identityServerUrl) {
      return;
    }

    const response = await fetch(
      `${this.configService.config.identityServerUrl}${ApplicationPaths.ApiAuthorizationClientConfigurationUrl}${this.configService.config.applicationName}`,
    );
    if (!response.ok) {
      throw new Error(`Could not load settings for '${this.configService.config.applicationName}'`);
    }

    const settings: any = await response.json();

    this._userManager = new UserManager(settings);

    this._userManager.events.addUserSignedOut(async () => {
      await this._userManager.removeUser();
      this.userSubject.next(undefined);
    });
  }

  private getUserFromStorage(): Observable<IUser> {
    return from(this.ensureUserManagerInitialized()).pipe(
      mergeMap(() => this._userManager.getUser()),
      mergeMap(() => this.getExternalUser()),
      map((u: any) => u && u.profile),
    );
  }

  private getExternalUser(): Observable<any> {
    const offlineToken = JSON.parse(sessionStorage.getItem(this.tokenKey));
    if (offlineToken) {
      const service = new JwtHelperService();
      const profile = service.decodeToken(offlineToken.access_token);
      return of({ profile });
    }
    return of(undefined);
  }
}
